import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Descriptions, Grid, Input, List, Modal, Row, Segmented, Space, Spin, Tag, Typography, theme } from 'antd';
import {
  CheckCircleFilled, CheckCircleOutlined, CloseCircleFilled, CloseCircleOutlined, CloseOutlined, StopOutlined,
} from '@ant-design/icons';
import {
  TEXTO_AVISO,
  enviarBienvenida,
  esEmailValido,
  esNombreValido,
  getVistaPreviaBienvenida,
  postVistaPreviaBienvenida,
} from '../../api/emailBienvenidaApi';
import type {
  DestinatarioBienvenidaVista,
  EmailBienvenidaVistaPrevia,
  EnvioBienvenidaResultado,
} from '../../api/emailBienvenidaApi';
import { errorOportunidad } from './errores';

const { Text } = Typography;

interface EmailBienvenidaModalProps {
  /** La venta recién creada; null, cerrado. */
  idOportunidad: number | null;
  onCerrar: () => void;
}

// La clave de un destinatario en la lista: su email o, sin él, su cliente.
const clave = (d: DestinatarioBienvenidaVista) => d.email?.toLowerCase() ?? `cliente-${d.idCliente}`;

const textoPara = (d: DestinatarioBienvenidaVista) => (d.email ? `${d.nombre} <${d.email}>` : d.nombre || d.nombreCompleto || 'Sin nombre');

/** Dónde se ve la vista previa: en el ancho del popup o en el de un teléfono (#264). */
type Dispositivo = 'PC' | 'Móvil';

/** El ancho de un teléfono corriente para la vista previa «Móvil». */
export const ANCHO_MOVIL = 375;

/** El ancho del popup con el resultado del envío, que es poco contenido (#270). */
export const ANCHO_RESULTADO = 480;

/**
 * El email de bienvenida tras el alta de una venta a iniciativa del cliente (#255, API de #254), a dos columnas (#264):
 * a la izquierda, De y Asunto de la plantilla, de solo lectura, y Para editable, con los avisos de cada destinatario; a la
 * derecha, la vista previa del elegido, aislada en un iframe sin scripts, en PC o en móvil. Abajo, «Enviar» o «Cancelar».
 * La oportunidad ya está guardada: se envíe o no, sigue ahí.
 */
export const EmailBienvenidaModal: React.FC<EmailBienvenidaModalProps> = ({ idOportunidad, onCerrar }) => {
  const [vista, setVista] = useState<EmailBienvenidaVistaPrevia | null>(null);
  const [destinatarios, setDestinatarios] = useState<DestinatarioBienvenidaVista[]>([]);
  const [elegido, setElegido] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [errorAlta, setErrorAlta] = useState<string | null>(null);
  const [anadiendo, setAnadiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [resultado, setResultado] = useState<EnvioBienvenidaResultado | null>(null);
  const [dispositivo, setDispositivo] = useState<Dispositivo>('PC');

  // Al abrirse, la vista previa de los titulares; al cerrarse, todo a cero.
  useEffect(() => {
    setVista(null); setDestinatarios([]); setElegido(null); setError(null); setResultado(null); setErrorEnvio(null);
    setNombre(''); setEmail(''); setErrorAlta(null);
    if (idOportunidad == null) return;
    let vigente = true;
    setCargando(true);
    getVistaPreviaBienvenida(idOportunidad)
      .then((v) => {
        if (!vigente) return;
        setVista(v);
        setDestinatarios(v.destinatarios);
        const primero = v.destinatarios.find((d) => d.enviable) ?? v.destinatarios[0];
        setElegido(primero ? clave(primero) : null);
      })
      .catch((e) => { if (vigente) setError(errorOportunidad(e).mensaje); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [idOportunidad]);

  const enviables = destinatarios.filter((d) => d.enviable && d.email);
  const ninguno = destinatarios.every((d) => !d.email);
  const visto = destinatarios.find((d) => clave(d) === elegido) ?? null;

  const quitar = (d: DestinatarioBienvenidaVista) => {
    const quedan = destinatarios.filter((x) => clave(x) !== clave(d));
    setDestinatarios(quedan);
    if (elegido === clave(d)) setElegido(quedan[0] ? clave(quedan[0]) : null);
  };

  // Un destinatario nuevo: con nombre y un email válido que no esté ya; el servidor da sus avisos y su vista previa.
  const anadir = async () => {
    if (!esNombreValido(nombre)) { setErrorAlta("Escribe el nombre del destinatario: solo letras, espacios, ' - y ."); return; }
    if (!esEmailValido(email)) { setErrorAlta('El email no es válido.'); return; }
    if (destinatarios.some((d) => d.email?.toLowerCase() === email.trim().toLowerCase())) {
      setErrorAlta('Ese email ya está en la lista.');
      return;
    }
    setErrorAlta(null);
    setAnadiendo(true);
    try {
      const v = await postVistaPreviaBienvenida(idOportunidad!, [{ nombre: nombre.trim(), email: email.trim() }]);
      const nuevo = v.destinatarios[0];
      setDestinatarios([...destinatarios, nuevo]);
      setElegido(clave(nuevo));
      setNombre(''); setEmail('');
    } catch (e) {
      setErrorAlta(errorOportunidad(e).mensaje);
    } finally {
      setAnadiendo(false);
    }
  };

  const enviar = async () => {
    setEnviando(true);
    setErrorEnvio(null);
    try {
      setResultado(await enviarBienvenida(idOportunidad!, enviables.map((d) => ({ idCliente: d.idCliente, nombre: d.nombre, email: d.email! }))));
    } catch (e) {
      setErrorEnvio(errorOportunidad(e).mensaje);
    } finally {
      setEnviando(false);
    }
  };

  // En pantalla ancha, dos columnas de alto fijo: la configuración, que se desplaza sola, y la vista previa, a todo el alto
  // (#264). En pantalla estrecha, una debajo de otra, sin desplazamiento horizontal. Sin pie: los botones van en la
  // columna izquierda (#268).
  const ancha = Grid.useBreakpoint().lg ?? false;
  const altoColumnas = 'calc(100vh - 210px)';

  return (
    <Modal
      open={idOportunidad != null}
      title="Email de bienvenida al cliente"
      // Antes de enviar, casi toda la pantalla (#264); con el resultado, un popup estrecho (#270), que Ant Design limita
      // al ancho de la pantalla en un móvil.
      width={resultado ? ANCHO_RESULTADO : 'min(1600px, calc(100vw - 32px))'}
      centered
      onCancel={onCerrar}
      footer={null}
      destroyOnClose
    >
      {cargando && <Spin />}
      {error && (
        <Space direction="vertical" size={16} style={{ width: '100%', alignItems: 'center' }}>
          <Alert type="error" showIcon message={error} style={{ width: '100%' }} />
          <Button type="primary" onClick={onCerrar}>Cerrar</Button>
        </Space>
      )}
      {vista && !error && (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {vista.modoPrueba && (
            <Alert
              type="info"
              showIcon
              message={`Entorno de pruebas: los envíos van al buzón de pruebas (${vista.buzonPruebas ?? 'sin configurar'}), no a los clientes.`}
            />
          )}
          {resultado ? <Resultados resultado={resultado} onCerrar={onCerrar} /> : (
            <Row gutter={[24, 16]}>
              <Col
                xs={24}
                lg={10}
                role="group"
                aria-label="Configuración del email"
                style={ancha ? { height: altoColumnas, overflowY: 'auto' } : undefined}
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="De">{vista.de.email ? `${vista.de.nombre} <${vista.de.email}>` : vista.de.nombre}</Descriptions.Item>
                    <Descriptions.Item label="Asunto">{visto?.asunto ?? vista.asunto}</Descriptions.Item>
                  </Descriptions>

                  <div>
                    <Text strong>Para</Text>
                    {ninguno && <Alert type="warning" showIcon message="No tenemos email del cliente." style={{ margin: '8px 0' }} />}
                    <List
                      size="small"
                      dataSource={destinatarios}
                      locale={{ emptyText: 'Sin destinatarios' }}
                      renderItem={(d) => (
                        <List.Item
                          aria-label={textoPara(d)}
                          style={{ cursor: 'pointer', background: clave(d) === elegido ? 'rgba(0,0,0,0.04)' : undefined }}
                          onClick={() => setElegido(clave(d))}
                          actions={[
                            <Button
                              key="quitar"
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              aria-label={`Quitar a ${d.nombre || d.nombreCompleto || 'este destinatario'}`}
                              onClick={(e) => { e.stopPropagation(); quitar(d); }}
                            />,
                          ]}
                        >
                          <Space wrap>
                            <Text>{textoPara(d)}</Text>
                            {d.avisos.map((a) => <Tag key={a} color="warning">{TEXTO_AVISO[a] ?? a}</Tag>)}
                            {!d.enviable && d.email && <Text type="secondary">No se le enviará.</Text>}
                          </Space>
                        </List.Item>
                      )}
                    />
                    <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                      <Input placeholder="Nombre" aria-label="Nombre del destinatario" value={nombre} onChange={(e) => setNombre(e.target.value)} />
                      <Input placeholder="Email" aria-label="Email del destinatario" value={email} onChange={(e) => setEmail(e.target.value)} onPressEnter={() => void anadir()} />
                      <Button onClick={() => void anadir()} loading={anadiendo}>Añadir</Button>
                    </Space.Compact>
                    {errorAlta && <Text type="danger" role="alert">{errorAlta}</Text>}
                  </div>

                  {errorEnvio && <Alert type="error" showIcon message={errorEnvio} />}

                  {/* Los botones, debajo de «Para» (#268): la columna derecha es solo la vista previa. */}
                  <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                    <Button onClick={onCerrar}>Cancelar</Button>
                    <Button type="primary" disabled={enviables.length === 0 || cargando} loading={enviando} onClick={() => void enviar()}>
                      Enviar
                    </Button>
                  </Space>
                </Space>
              </Col>

              <Col
                xs={24}
                lg={14}
                role="group"
                aria-label="Vista previa"
                style={ancha ? { height: altoColumnas, display: 'flex', flexDirection: 'column' } : undefined}
              >
                {visto && (
                  <>
                    <Space wrap style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }}>
                      <Text strong>Vista previa para {textoPara(visto)}</Text>
                      <Segmented
                        aria-label="Ver la vista previa en"
                        value={dispositivo}
                        onChange={(v) => setDispositivo(v as Dispositivo)}
                        options={['PC', 'Móvil']}
                      />
                    </Space>
                    {/* Aislada: sin scripts ni acceso a la página (sandbox vacío). En «Móvil», con el ancho de un teléfono. */}
                    <iframe
                      title="Vista previa del email"
                      sandbox=""
                      srcDoc={visto.html}
                      style={{
                        width: dispositivo === 'Móvil' ? ANCHO_MOVIL : '100%',
                        maxWidth: '100%',
                        height: ancha ? undefined : '70vh',
                        flex: ancha ? 1 : undefined,
                        alignSelf: 'center',
                        display: 'block',
                        margin: '0 auto',
                        border: '1px solid rgba(0,0,0,0.15)',
                      }}
                    />
                  </>
                )}
              </Col>
            </Row>
          )}
        </Space>
      )}
    </Modal>
  );
};

// El resultado tras «Enviar», en el popup estrecho (#268, #270): un icono que se lee de un vistazo (éxito si salieron
// todos, error si alguno falló o no se envió), el de cada destinatario debajo, y «Cerrar».
const Resultados: React.FC<{ resultado: EnvioBienvenidaResultado; onCerrar: () => void }> = ({ resultado, onCerrar }) => {
  const { token } = theme.useToken();
  const todos = resultado.resultados.length > 0 && resultado.resultados.every((r) => r.resultado === 'enviado');
  return (
    <Space direction="vertical" align="center" size={12} style={{ width: '100%', margin: '16px 0', display: 'flex' }}>
      <span role="img" aria-label={todos ? 'Envío correcto' : 'Envío con errores'}>
        {todos
          ? <CheckCircleFilled style={{ fontSize: 48, color: token.colorSuccess }} />
          : <CloseCircleFilled style={{ fontSize: 48, color: token.colorError }} />}
      </span>
      <Text strong style={{ fontSize: 16 }}>{todos ? 'Email enviado' : 'Algún email no se ha enviado'}</Text>
      <List
        size="small"
        style={{ width: '100%' }}
        dataSource={resultado.resultados}
        renderItem={(r) => (
          <List.Item>
            <Space>
              {r.resultado === 'enviado' && <CheckCircleOutlined aria-hidden style={{ color: token.colorSuccess }} />}
              {r.resultado === 'error' && <CloseCircleOutlined aria-hidden style={{ color: token.colorError }} />}
              {r.resultado === 'rechazado' && <StopOutlined aria-hidden style={{ color: token.colorError }} />}
              <Text>{`${r.nombre} <${r.email}>`}</Text>
              <Text type={r.resultado === 'enviado' ? 'success' : 'danger'}>
                {r.resultado === 'enviado' ? 'Enviado' : r.resultado === 'error' ? 'No se pudo enviar' : 'No enviado'}
                {r.detalle ? `: ${r.detalle.split(', ').map((d) => TEXTO_AVISO[d] ?? d).join('; ')}` : ''}
                {r.resultado === 'enviado' && resultado.modoPrueba && r.enviadoA ? ` (al buzón de pruebas, ${r.enviadoA})` : ''}
              </Text>
            </Space>
          </List.Item>
        )}
      />
      <Button type="primary" onClick={onCerrar}>Cerrar</Button>
    </Space>
  );
};
